import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PatientJourneys } from "../components/PatientJourneys";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
    Activity, ArrowRight, BadgeCheck, Check, ChevronRight, Heart, Leaf, Scale, Scissors,
    Shield, Sparkles, Stethoscope, Sun, Table2, TriangleAlert, X,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { formatINR } from "../lib/constants";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = { scissors: Scissors, scale: Scale, sun: Sun, shield: Shield, activity: Activity, heart: Heart, leaf: Leaf, sparkles: Sparkles };

const Section = ({ children, className = "" }) => (
    <section className={`mx-auto max-w-7xl px-6 md:px-12 ${className}`}>{children}</section>
);

const DoctorCard = ({ doctor, active, onSelect }) => (
    <button
        onClick={() => onSelect(doctor)}
        data-testid={`doctor-card-${doctor.id}`}
        className={`w-full text-left p-4 rounded-sm border transition-colors ${
            active ? "border-gold/60 bg-gold/[0.07]" : "border-white/10 bg-bg1 hover:border-white/25"
        }`}
    >
        <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                {doctor.photo_url ? <img src={doctor.photo_url} alt="" className="w-full h-full object-cover" />
                    : <Stethoscope className="w-5 h-5 text-zinc-400" />}
            </div>
            <div className="min-w-0">
                <div className="text-sm text-white truncate">{doctor.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{doctor.qualification || doctor.title || "Consultant"}</div>
            </div>
            {active && <BadgeCheck className="w-4 h-4 text-gold ml-auto shrink-0" />}
        </div>
    </button>
);

export default function Consultation({ embedded = false }) {
    const { customer, loginWithGoogle } = useCustomerAuth();
    const [doctor, setDoctor] = useState(null);
    const [showAllAreas, setShowAllAreas] = useState(false);
    const [compare, setCompare] = useState(false);
    const [selected, setSelected] = useState(null);

    const { data, isLoading } = useQuery({
        queryKey: ["consult-config"],
        queryFn: async () => (await fetch(`${API}/api/consult/config`)).json(),
    });

    const c = data?.content || {};
    const doctors = data?.doctors || [];
    const packages = data?.packages || [];
    const areas = data?.areas || [];
    const active = useMemo(() => doctor || doctors[0] || null, [doctor, doctors]);
    const visibleAreas = showAllAreas ? areas : areas.slice(0, 6);
    const preselectedArea = new URLSearchParams(window.location.search).get("area") || "";

    // Arriving from a consultation pack: show and highlight that area.
    useEffect(() => {
        if (!preselectedArea || !areas.length) return;
        if (!areas.slice(0, 6).some((a) => a.key === preselectedArea)) setShowAllAreas(true);
        const t = setTimeout(() => {
            document.querySelector(`[data-testid="area-card-${preselectedArea}"]`)
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 400);
        return () => clearTimeout(t);
    }, [preselectedArea, areas]);

    const startBooking = (pkg, areaKey = null) => {
        sessionStorage.setItem("rhm_consult_selection", JSON.stringify({
            package_key: pkg?.key || null, doctor_id: active?.id || null, area_key: areaKey,
        }));
        if (!pkg) { scrollToPackages(); return; }
        setSelected({ pkg, areaKey });
    };

    const scrollToPackages = () => document.getElementById("packages")?.scrollIntoView({ behavior: "smooth", block: "start" });

    if (isLoading) return <div className="pt-40 pb-40 text-center text-zinc-500">Loading consultation…</div>;

    return (
        <div className={`${embedded ? "pt-8" : "pt-28 md:pt-32"} pb-24`} data-testid="consultation-page">
            {/* HERO */}
            <Section>
                <div className="grid lg:grid-cols-[1fr_320px] gap-10 items-start">
                    <div>
                        <div className="eyebrow mb-4">{c.hero_eyebrow}</div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight max-w-2xl">
                            {c.hero_title}
                        </h1>
                        <p className="mt-5 text-gold text-base md:text-lg tracking-[0.15em] uppercase">{c.hero_subtitle}</p>

                        {active && (
                            <div className="mt-8 p-6 rounded-sm border border-white/10 bg-bg1" data-testid="active-doctor-panel">
                                <div className="flex items-start gap-4">
                                    <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                                        {active.photo_url ? <img src={active.photo_url} alt={active.name} className="w-full h-full object-cover" />
                                            : <Stethoscope className="w-7 h-7 text-zinc-400" />}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-display text-2xl tracking-tight">{active.name}</div>
                                        <div className="text-xs text-gold uppercase tracking-[0.18em] mt-1">{active.title}</div>
                                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-400">
                                            {active.qualification && <span>{active.qualification}</span>}
                                            {active.experience_years ? <span>{active.experience_years} years experience</span> : null}
                                            {active.patients_treated && <span>{active.patients_treated} patients</span>}
                                            {active.city && <span>{active.city}</span>}
                                        </div>
                                        {active.bio && <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{active.bio}</p>}
                                        {!!active.specialities?.length && (
                                            <div className="mt-4 flex flex-wrap gap-1.5">
                                                {active.specialities.map((s) => (
                                                    <span key={s} className="text-[10px] uppercase tracking-[0.16em] border border-white/12 px-2.5 py-1 rounded-full text-zinc-300">{s}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <button onClick={scrollToPackages} data-testid="hero-choose-consultation-button" className="btn-pill btn-primary">
                                {c.hero_cta} <ArrowRight className="w-4 h-4 ml-2 inline" />
                            </button>
                            <button onClick={() => setCompare(true)} data-testid="open-compare-button"
                                className="btn-pill btn-secondary inline-flex items-center gap-2">
                                <Table2 className="w-4 h-4" /> Compare Plans
                            </button>
                        </div>
                    </div>

                    {/* DOCTOR SIDEBAR */}
                    <aside className="lg:sticky lg:top-28" data-testid="doctor-sidebar">
                        <div className="mono text-[10px] tracking-[0.22em] text-zinc-500 mb-3">CHOOSE YOUR CONSULTANT</div>
                        <div className="space-y-2.5">
                            {doctors.map((d) => (
                                <DoctorCard key={d.id} doctor={d} active={active?.id === d.id} onSelect={setDoctor} />
                            ))}
                        </div>
                        <p className="mt-4 text-xs text-zinc-400 leading-relaxed">
                            Your selected consultant is carried through booking, intake and follow-ups.
                        </p>
                    </aside>
                </div>
            </Section>

            {/* HOW IT WORKS */}
            <Section className="mt-12 md:mt-16">
                <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight">{c.steps_title}</h2>
                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {(c.steps || []).map((s, i) => (
                        <motion.div key={s.label || i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                            className="relative p-6 rounded-sm border border-white/10 bg-bg1 overflow-hidden">
                            <div className="absolute -right-2 -top-3 font-display text-6xl text-white/[0.04]">{s.label}</div>
                            <div className="mono text-[10px] tracking-[0.22em] text-gold">{s.label}</div>
                            <div className="mt-3 font-display text-xl uppercase tracking-tight">{s.title}</div>
                            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.body}</p>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* AREAS */}
            <Section className="mt-12 md:mt-16">
                <div className="max-w-2xl">
                    <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight">{c.areas_title}</h2>
                    {c.areas_subtitle && <p className="mt-4 text-zinc-400">{c.areas_subtitle}</p>}
                </div>
                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="areas-grid">
                    {visibleAreas.map((a) => {
                        const Icon = ICONS[a.icon] || Sparkles;
                        return (
                            <button key={a.id} onClick={() => startBooking(null, a.key)}
                                data-testid={`area-card-${a.key}`}
                                className={`group text-left p-6 rounded-sm border bg-bg1 transition-colors ${
                                    a.key === preselectedArea
                                        ? "border-gold ring-1 ring-gold/40"
                                        : "border-white/10 hover:border-gold/40"}`}>
                                <Icon className="w-6 h-6 text-gold" />
                                <div className="mt-4 font-display text-xl uppercase tracking-tight">{a.name}</div>
                                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{a.description}</p>
                                {!!a.concerns?.length && (
                                    <div className="mt-3 text-[11px] text-zinc-400">{a.concerns.slice(0, 3).join(" • ")}</div>
                                )}
                                <span className="mt-5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-lime">
                                    Consult About This <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                </span>
                            </button>
                        );
                    })}
                </div>
                {areas.length > 6 && (
                    <button onClick={() => setShowAllAreas(!showAllAreas)} data-testid="toggle-all-areas-button"
                        className="mt-6 mono text-[10px] tracking-[0.2em] text-gold hover:text-white transition-colors">
                        {showAllAreas ? "SHOW FEWER AREAS" : `VIEW ALL AREAS (${areas.length})`}
                    </button>
                )}
            </Section>

            {/* PACKAGES */}
            <Section className="mt-12 md:mt-16">
                <div id="packages" className="scroll-mt-28 max-w-2xl">
                    <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight">{c.packages_title}</h2>
                    {c.packages_subtitle && <p className="mt-4 text-zinc-400">{c.packages_subtitle}</p>}
                </div>
                <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="packages-grid">
                    {packages.map((p) => (
                        <div key={p.id} data-testid={`package-card-${p.key}`}
                            className={`relative flex flex-col p-6 rounded-sm border ${
                                p.featured ? "border-gold/50 bg-gradient-to-b from-gold/[0.12] to-transparent" : "border-white/10 bg-bg1"
                            }`}>
                            {p.badge && (
                                <span className="absolute -top-3 left-6 bg-gold text-bg0 text-[9px] uppercase tracking-[0.2em] font-bold px-2.5 py-1">
                                    {p.badge}
                                </span>
                            )}
                            {p.suitable_for && <div className="mono text-[9px] tracking-[0.2em] text-zinc-500">{p.suitable_for.toUpperCase()}</div>}
                            <h3 className="mt-2 font-display text-2xl uppercase tracking-tight leading-tight">{p.name}</h3>
                            <div className="mt-3 font-display text-4xl" data-testid={`package-price-${p.key}`}>{formatINR(p.price)}</div>
                            <div className="mt-1 text-xs text-zinc-400">{p.duration_label}</div>
                            <div className="text-[11px] text-gold uppercase tracking-[0.16em] mt-1">{p.validity_label}</div>
                            <ul className="mt-5 space-y-2 flex-1">
                                {(p.includes || []).slice(0, 5).map((inc) => (
                                    <li key={inc} className="flex gap-2.5 text-sm text-zinc-300">
                                        <Check className="w-3.5 h-3.5 text-lime mt-1 shrink-0" /> <span>{inc}</span>
                                    </li>
                                ))}
                            </ul>
                            <button onClick={() => startBooking(p)} data-testid={`package-cta-${p.key}`}
                                className={`mt-6 w-full btn-pill ${p.featured ? "btn-primary" : "btn-secondary"}`}>
                                {p.cta_label || "Consult Now"} — {formatINR(p.price)}
                            </button>
                        </div>
                    ))}
                </div>
                <button onClick={() => setCompare(true)} data-testid="compare-plans-link"
                    className="mt-6 mono text-[10px] tracking-[0.2em] text-gold hover:text-white transition-colors">
                    COMPARE CONSULTATION PLANS →
                </button>
                {c.medical_disclaimer && <p className="mt-6 text-xs text-zinc-400 max-w-3xl leading-relaxed">{c.medical_disclaimer}</p>}
            </Section>

            {/* WHY */}
            <Section className="mt-12 md:mt-16">
                <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight">{c.why_title}</h2>
                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {(c.why_points || []).map((w) => (
                        <div key={w.title} className="p-6 rounded-sm border border-white/10 bg-bg1">
                            <BadgeCheck className="w-5 h-5 text-lime" />
                            <div className="mt-4 font-display text-lg uppercase tracking-tight">{w.title}</div>
                            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{w.body}</p>
                        </div>
                    ))}
                </div>
            </Section>

            <PatientJourneys title={c.journeys_title} subtitle={c.journeys_subtitle} />

            {/* FAQ */}
            <Section className="mt-12 md:mt-16">
                <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight">{c.faq_title}</h2>
                <Accordion type="single" collapsible className="mt-6 max-w-3xl">
                    {(c.faqs || []).map((f, i) => (
                        <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                            <AccordionTrigger data-testid={`consult-faq-${i}`} className="text-sm text-left">{f.question}</AccordionTrigger>
                            <AccordionContent className="text-zinc-400">{f.answer}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </Section>

            {/* EMERGENCY + FINAL CTA */}
            <Section className="mt-16">
                {c.emergency_notice && (
                    <div className="flex items-start gap-3 border border-red-500/40 bg-red-500/[0.07] px-5 py-4 max-w-3xl" data-testid="emergency-notice">
                        <TriangleAlert className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                        <p className="text-sm text-red-300 leading-relaxed">{c.emergency_notice}</p>
                    </div>
                )}
                <div className="mt-16 p-10 md:p-14 rounded-sm border border-white/10 bg-bg1 text-center">
                    <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight">{c.final_cta_title}</h2>
                    <p className="mt-4 text-zinc-400">{c.final_cta_body}</p>
                    <button onClick={scrollToPackages} data-testid="final-cta-button" className="mt-8 btn-pill btn-primary">
                        {c.final_cta_label}
                    </button>
                </div>
            </Section>

            {/* SELECTION CONFIRMATION */}
            {selected && (
                <div className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
                    data-testid="selection-modal" onClick={() => setSelected(null)}>
                    <div className="w-full max-w-md bg-bg1 border border-white/12 p-7" onClick={(e) => e.stopPropagation()}>
                        <div className="eyebrow mb-3">You selected</div>
                        <h3 className="font-display text-3xl uppercase tracking-tight">{selected.pkg.name}</h3>
                        <div className="mt-2 font-display text-3xl text-gold">{formatINR(selected.pkg.price)}</div>
                        <div className="mt-1 text-sm text-zinc-400">{selected.pkg.duration_label}</div>
                        <div className="text-[11px] text-gold uppercase tracking-[0.16em] mt-1">{selected.pkg.validity_label}</div>
                        <div className="mt-5 pt-5 border-t border-white/10 space-y-1.5 text-sm text-zinc-300">
                            <div>Consultant: <span className="text-white">{active?.name}</span></div>
                            {selected.areaKey && <div>Area: <span className="text-white">{areas.find((a) => a.key === selected.areaKey)?.name}</span></div>}
                        </div>
                        <div className="mt-6 p-4 border border-gold/30 bg-gold/[0.06] text-xs text-zinc-300 leading-relaxed">
                            Next: pick an appointment slot from your consultant's live availability, share a few details
                            and complete the patient intake form. No account needed — we create one for you.
                        </div>
                        <div className="mt-5 flex flex-col gap-2">
                            <Link to="/consultation/book"
                                data-testid="selection-contact-button" className="btn-pill btn-primary text-center">
                                Book Consultation
                            </Link>
                            {!customer && (
                                <button onClick={loginWithGoogle} data-testid="consult-google-button"
                                    className="btn-pill btn-secondary">
                                    1-Tap Google Fill ⚡
                                </button>
                            )}
                            <button onClick={() => setSelected(null)} data-testid="selection-close-button"
                                className="btn-pill btn-secondary">Keep Browsing</button>
                        </div>
                    </div>
                </div>
            )}

            {/* COMPARE MODAL */}
            {compare && (
                <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
                    data-testid="compare-modal" onClick={() => setCompare(false)}>
                    <div className="w-full max-w-4xl bg-bg1 border border-white/12 p-6 md:p-8 my-10" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between gap-4 mb-6">
                            <h3 className="font-display text-2xl uppercase tracking-tight">Compare Consultation Plans</h3>
                            <button onClick={() => setCompare(false)} data-testid="close-compare-button"
                                className="w-9 h-9 border border-white/15 flex items-center justify-center text-zinc-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/10 text-left">
                                        <th className="mono text-[10px] tracking-[0.16em] text-zinc-500 font-normal py-3 pr-4">Feature</th>
                                        {["Basic", "Comprehensive", "Long-Term", "Family"].map((h) => (
                                            <th key={h} className="mono text-[10px] tracking-[0.16em] text-zinc-500 font-normal py-3 px-3 whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {(c.compare_rows || []).map((row, i) => (
                                        <tr key={i}>
                                            <td className="py-3 pr-4 text-white whitespace-nowrap">{row.feature}</td>
                                            <td className="py-3 px-3 text-zinc-300">{row.basic}</td>
                                            <td className="py-3 px-3 text-zinc-300">{row.comprehensive}</td>
                                            <td className="py-3 px-3 text-zinc-300">{row.long_term}</td>
                                            <td className="py-3 px-3 text-zinc-300">{row.family}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
